import { useEffect, useMemo, useState } from 'react'
import { Loader2, AlertCircle, Check } from 'lucide-react'
import { useAuthStore } from './authStore'
import { getSupabaseClient } from '@/repository/supabase/client'

/**
 * Lands the magic-link redirect.
 *
 * Supabase JS reads `?code=...` (or the older `#access_token=...` hash)
 * automatically when `detectSessionInUrl: true` is set on the client.
 * By the time React mounts, one of three things has happened:
 *
 *   1. **Success.** The session is established; the AuthProvider's
 *      `onAuthStateChange` will set `session` non-null and we redirect
 *      home.
 *   2. **Hash-encoded error from Supabase.** The URL contains
 *      `#error=...&error_description=...` — typically because the
 *      magic link expired, was already used (one-shot), or the OTP
 *      was invalidated by a fresh sign-in elsewhere. Surface the
 *      error immediately with a friendly translation so the user
 *      knows to request a new link rather than waiting on a spinner
 *      that's never resolving.
 *   3. **Nothing.** Network blip, slow Supabase, etc. After the
 *      timeout we show the generic "didn't work" UI.
 */
const REDIRECT_TIMEOUT_MS = 5000

interface HashError {
  /** Short user-facing summary. */
  title: string
  /** Long explanation with what to do next. */
  body: string
  /** The raw error code from Supabase, for the technical-details disclosure. */
  rawCode: string
  rawDescription: string
}

export function AuthCallbackPage() {
  const session = useAuthStore((s) => s.session)
  const isLoading = useAuthStore((s) => s.isLoading)
  const [timedOut, setTimedOut] = useState(false)
  const [exchangeDiagnosis, setExchangeDiagnosis] = useState<ExchangeDiagnosis | null>(null)

  // Parse the URL hash on first render. The hash is what Supabase uses
  // for both success (#access_token=...) and failure (#error=...) cases.
  // Reading it synchronously here means we can render the error UI on
  // first paint — no spinner flicker when we already know we've failed.
  const hashError = useMemo(() => parseHashError(), [])

  // Capture initial URL query state BEFORE Supabase JS's `detectSessionInUrl`
  // strips the `?code=` parameter post-exchange. Without this snapshot the
  // timeout diagnostic can't tell whether the user landed here via a real
  // magic-link redirect or just typed `/auth/callback` directly.
  const initialQuery = useMemo(() => captureInitialQuery(), [])

  // v0.8.28 — derive an immediate diagnosis at render time when the URL
  // arrived with no sign-in info AT ALL (no `?code=`, no `#access_token=`).
  // There's nothing for Supabase JS to exchange in that case, so the
  // 5-second timeout wait was just dead air. Computing this via `useMemo`
  // (instead of setState-in-effect) keeps `react-hooks/set-state-in-effect`
  // happy.
  const immediateNoInfoDiagnosis = useMemo(
    () =>
      !initialQuery.hasCode && !initialQuery.hashHasToken
        ? buildNoSignInInfoDiagnosis(initialQuery)
        : null,
    [initialQuery]
  )

  useEffect(() => {
    if (session) {
      // Hard-redirect rather than push() so the URL `?code=...` is wiped
      // from history. Hard redirect also re-runs the boot path so
      // anything that depends on session-on-mount works cleanly.
      window.location.replace('/')
      return
    }
    if (isLoading) return // wait until AuthProvider hydration settles
    if (hashError) return // already showing the error; no timeout needed
    // No URL info at mount — diagnostic is already rendered via the
    // derived `immediateNoInfoDiagnosis`; no timeout needed.
    if (immediateNoInfoDiagnosis) return

    const t = setTimeout(async () => {
      // v0.8.26 — run a real diagnosis instead of the generic "expired"
      // fallback. Pre-fix the user got "may have expired or already been
      // used" even on links they'd clicked within the minute, which made
      // a PKCE-verifier-mismatch (the actual common failure mode) look
      // like an expiry — actively misleading. Defensive try/catch so a
      // bug in the diagnostic itself doesn't strand the user on the
      // "Signing you in…" spinner forever.
      let diagnosis: ExchangeDiagnosis
      try {
        diagnosis = await diagnoseExchangeFailure(initialQuery)
      } catch (err) {
        diagnosis = {
          title: 'Sign-in didn’t complete',
          body: 'Something went wrong while diagnosing the sign-in. Request a fresh magic link and try again.',
          technical: err instanceof Error ? err.message : String(err),
        }
      }
      setExchangeDiagnosis(diagnosis)
      setTimedOut(true)
    }, REDIRECT_TIMEOUT_MS)
    return () => clearTimeout(t)
  }, [session, isLoading, hashError, initialQuery, immediateNoInfoDiagnosis])

  // Hash-encoded error from Supabase — surface it immediately.
  if (hashError && !session) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-950 p-6">
        <div className="max-w-md w-full bg-slate-900 border border-red-500/30 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-white">{hashError.title}</h1>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">{hashError.body}</p>
          <a
            href="/"
            className="inline-block mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium"
          >
            Back to DebtOS
          </a>
          {hashError.rawCode || hashError.rawDescription ? (
            <details className="mt-4 text-left">
              <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-300">
                Technical details
              </summary>
              <div className="mt-2 p-2 bg-slate-950/60 border border-slate-800 rounded text-[10px] text-slate-400 font-mono break-words">
                {hashError.rawCode ? <div>code: {hashError.rawCode}</div> : null}
                {hashError.rawDescription ? <div>desc: {hashError.rawDescription}</div> : null}
              </div>
            </details>
          ) : null}
        </div>
      </div>
    )
  }

  // v0.8.26 — no hash error but timed out. Render a DIAGNOSED message based
  // on what we found instead of the prior misleading "may have expired"
  // copy that fired even on fresh-from-the-minute links. v0.8.28 — also
  // covers the immediate-render path when `immediateNoInfoDiagnosis` fires:
  // no point waiting 5s when the URL had no sign-in info to begin with.
  const renderableDiagnosis = immediateNoInfoDiagnosis ?? (timedOut ? exchangeDiagnosis : null)
  if (renderableDiagnosis && !session) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-950 p-6">
        <div className="max-w-md w-full bg-slate-900 border border-red-500/30 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-white">{renderableDiagnosis.title}</h1>
          {/* v0.8.28 — `whitespace-pre-line` honors the `\n\n` paragraph breaks
              in `buildNoSignInInfoDiagnosis`'s multi-bullet body so the user
              sees the cause list as separate lines, not a wall of text. */}
          <p className="text-sm text-slate-400 mt-2 leading-relaxed whitespace-pre-line text-left">
            {renderableDiagnosis.body}
          </p>
          <a
            href="/"
            className="inline-block mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium"
          >
            Back to DebtOS
          </a>
          {renderableDiagnosis.technical ? (
            <details className="mt-4 text-left">
              <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-300">
                Technical details
              </summary>
              <div className="mt-2 p-2 bg-slate-950/60 border border-slate-800 rounded text-[10px] text-slate-400 font-mono break-words whitespace-pre-wrap">
                {renderableDiagnosis.technical}
              </div>
            </details>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-slate-950 p-6">
      <div className="max-w-md w-full bg-slate-900 border border-slate-700 rounded-xl p-6 text-center">
        {session ? (
          <>
            <Check className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-white">Signed in</h1>
            <p className="text-sm text-slate-400 mt-2">Taking you back to your dashboard…</p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-blue-400 mx-auto mb-3 animate-spin" />
            <h1 className="text-lg font-semibold text-white">Signing you in…</h1>
            <p className="text-sm text-slate-400 mt-2">This should only take a second.</p>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Reads `error`, `error_code`, `error_description` from `window.location.hash`
 * and translates them into a friendly title + body. Returns `null` if no
 * error is present (the success or pending cases).
 *
 * Error codes we've seen in the wild and what to say about them:
 *   - `access_denied` / `otp_expired` — magic link expired (>1 hr old) or
 *     already used (one-shot tokens)
 *   - `invalid_request` / `invalid_grant` — link tampered with or
 *     project-config mismatch (URL allowlist edited mid-flight)
 *   - everything else — pass through with a generic frame
 */
function parseHashError(): HashError | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  if (!hash || !hash.includes('error')) return null

  // `URLSearchParams` parses query-string-shaped strings; the hash after `#`
  // is in that exact shape (`error=foo&error_description=bar`).
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const error = params.get('error')
  if (!error) return null

  const rawCode = params.get('error_code') ?? ''
  const rawDescription = params.get('error_description') ?? ''

  // Most common case: link expired or already clicked.
  if (
    error === 'access_denied' ||
    /otp_expired|expired|already.{0,5}used|invalid_otp/i.test(rawCode + rawDescription)
  ) {
    return {
      title: 'Sign-in link expired or already used',
      body: 'Magic links are single-use and expire after about an hour. Request a fresh one from the Sign in dialog — the new link will work on this device.',
      rawCode,
      rawDescription,
    }
  }

  if (/invalid_request|invalid_grant/i.test(error)) {
    return {
      title: 'Sign-in link was invalid',
      body: 'The link looks malformed or doesn’t match this project. If you have multiple DebtOS environments, make sure you opened the link from the email that matches this URL.',
      rawCode: error,
      rawDescription,
    }
  }

  // Fallback — pass Supabase's text through with a friendly wrapper.
  return {
    title: 'Sign-in failed',
    body:
      rawDescription ||
      'The sign-in attempt didn’t complete. Request a fresh magic link and try again.',
    rawCode: error,
    rawDescription,
  }
}

// ─── v0.8.26 timeout diagnosis ──────────────────────────────────────────

interface InitialQuery {
  hasCode: boolean
  /** Raw `?code=` value preserved before Supabase JS strips it from the URL. */
  code: string | null
  /**
   * True if the URL hash contained a Supabase implicit-flow success token
   * (`#access_token=...`) at mount. Even though our client config defaults
   * to PKCE, some redirect chains end up putting the token in the hash —
   * checking both makes the "no sign-in info" diagnosis defensible.
   */
  hashHasToken: boolean
  /**
   * Sanitized list of search-param keys present at mount (NOT values — values
   * may carry the security code). Surfaced in technical details so a
   * post-failure inspection shows whether the link arrived with the expected
   * parameter shape (just `code` for PKCE, vs. an unusual mix from a
   * link-scanning proxy).
   */
  searchKeys: string[]
  href: string
}

/**
 * Snapshot the URL query state on first render so we can diagnose
 * post-timeout failures even though Supabase JS's `detectSessionInUrl`
 * will have rewritten `window.location` by then.
 */
function captureInitialQuery(): InitialQuery {
  if (typeof window === 'undefined') {
    return { hasCode: false, code: null, hashHasToken: false, searchKeys: [], href: '' }
  }
  const url = new URL(window.location.href)
  const searchKeys: string[] = []
  url.searchParams.forEach((_value, key) => {
    searchKeys.push(key)
  })
  return {
    hasCode: url.searchParams.has('code'),
    code: url.searchParams.get('code'),
    hashHasToken: /access_token=/.test(url.hash),
    searchKeys,
    href: window.location.href,
  }
}

/**
 * v0.8.28 — Diagnosis for the "URL arrived with no sign-in info" case.
 * Separated out so the immediate-render path (no-code on mount) and the
 * timeout-fallback path render the same actionable copy.
 *
 * Pre-fix the user got "Sign-in link expired or already been used" on a
 * fresh link from the minute, then v0.8.26 changed that to a literal
 * "No sign-in code in the URL" — accurate but not actionable. The new
 * copy names the most likely cause (email-client link scanning consumes
 * the one-shot PKCE code before the user's click reaches the page) and
 * gives a concrete next step.
 */
function buildNoSignInInfoDiagnosis(initial: InitialQuery): ExchangeDiagnosis {
  return {
    title: 'Sign-in link didn’t carry a code',
    body:
      'The page expects a one-shot security code in the URL but didn’t find one. The most common causes:\n\n' +
      '• Your email provider (Outlook safe-links, some corporate scanners, certain Gmail prefetches) clicked the link to scan it BEFORE you did. Supabase codes are one-shot — the scan consumes the code and your click arrives empty-handed.\n\n' +
      '• You opened a bookmark or refreshed /auth/callback in an old tab.\n\n' +
      'Request a fresh sign-in link. If you suspect link scanning, copy the link from the email and paste it into your browser address bar instead of clicking — that skips most scanners.',
    technical:
      `searchKeys: [${initial.searchKeys.join(', ') || '(none)'}]` +
      `\nhashHasToken: ${initial.hashHasToken}` +
      `\nhref: ${initial.href}`,
  }
}

interface ExchangeDiagnosis {
  /** Short user-facing title for the failure mode. */
  title: string
  /** Long explanation with what to do next. */
  body: string
  /** Optional technical details for a disclosure block. */
  technical?: string
}

/**
 * Investigates why the magic-link exchange never produced a session
 * after the 5-second wait. Distinguishes the common modes so users get
 * an honest answer instead of the prior catch-all "may have expired"
 * copy that misdiagnosed PKCE-verifier-mismatch as expiry.
 *
 * Modes (in order of probability):
 *   1. **No code in URL.** User landed on `/auth/callback` directly
 *      (refresh after success, deep-link, etc.). Not a sign-in attempt.
 *   2. **Code present but PKCE verifier missing in localStorage.** The
 *      link was clicked from a DIFFERENT browser/device than the one
 *      that initiated the sign-in. PKCE verifiers are local to the
 *      requesting browser; without it, the exchange can't complete.
 *      Most common real-world failure for "I clicked a fresh link and
 *      it errored."
 *   3. **Verifier present, exchange explicitly retried, error captured.**
 *      Network blip, redirect-URL allowlist mismatch, expired token, or
 *      a code that was already exchanged.
 *   4. **Everything looks fine but no session.** Generic fallback.
 */
async function diagnoseExchangeFailure(initial: InitialQuery): Promise<ExchangeDiagnosis> {
  if (!initial.hasCode) {
    // v0.8.28 — share the same actionable copy whether we arrived at the
    // no-code state immediately (mount-time check) or after the timeout.
    return buildNoSignInInfoDiagnosis(initial)
  }

  const verifierKey = findPkceVerifierKey()
  if (!verifierKey) {
    return {
      title: 'Sign-in link opened in a different browser',
      body:
        'Magic links carry a security code that only the browser you started in can complete. ' +
        'If you opened the link inside the Gmail app, on a different device, or in a webview, the sign-in can’t finish. ' +
        'Open the link in the same browser where you requested it — or request a fresh link from the device you want to sign in on.',
      technical: `URL had ?code=${truncate(initial.code, 12)} but no PKCE verifier key was found in localStorage.`,
    }
  }

  // Verifier exists — explicitly re-attempt the exchange so we can capture
  // the real error message Supabase returns. detectSessionInUrl already
  // tried once silently; calling again either succeeds (race won) or
  // returns the actual error.
  const client = getSupabaseClient()
  if (!client) {
    return {
      title: 'Cloud sync not configured for this build',
      body: 'The Supabase environment variables are missing on this deployment. Sign-in is only available where they are set.',
    }
  }

  let exchangeError: { name: string; message: string; status?: number } | null = null
  try {
    if (initial.code) {
      const { error } = await client.auth.exchangeCodeForSession(initial.code)
      if (error) {
        exchangeError = {
          name: error.name ?? 'AuthError',
          message: error.message ?? String(error),
          status: (error as { status?: number }).status,
        }
      }
    }
  } catch (err) {
    exchangeError = {
      name: err instanceof Error ? err.name : 'Error',
      message: err instanceof Error ? err.message : String(err),
    }
  }

  if (exchangeError) {
    // Specific common cases — give targeted copy
    const msg = exchangeError.message.toLowerCase()
    if (msg.includes('code') && (msg.includes('used') || msg.includes('expired'))) {
      return {
        title: 'Sign-in link already used',
        body: 'This link has already completed a sign-in (links are single-use) or it timed out. Request a fresh one from the Sign in dialog.',
        technical: formatExchangeError(exchangeError),
      }
    }
    if (msg.includes('redirect') || msg.includes('allowlist') || msg.includes('not allowed')) {
      return {
        title: 'Sign-in link domain not allowed',
        body:
          'The Supabase project’s redirect-URL allowlist doesn’t include this domain. ' +
          'If you control the project: add this URL to Auth → URL Configuration → Redirect URLs.',
        technical: formatExchangeError(exchangeError),
      }
    }
    return {
      title: 'Sign-in link couldn’t be exchanged',
      body:
        'The auth server rejected the code. This is usually a network blip or a stale link — ' +
        'request a fresh one and try again from the same browser.',
      technical: formatExchangeError(exchangeError),
    }
  }

  // Verifier present, exchange returned no error, still no session. Race
  // condition (the session arrived between timeout fire and exchange retry)
  // — the redirect should kick in any moment, but show a neutral message.
  return {
    title: 'Sign-in taking longer than expected',
    body: 'The link processed without an error but the session hasn’t landed yet. Try refreshing the page; if it still doesn’t work, request a fresh link.',
    technical: 'No exchange error but session is still null after retry.',
  }
}

/**
 * Looks up the Supabase PKCE code-verifier key in localStorage. The key
 * is namespaced by project ref — `sb-<ref>-auth-token-code-verifier` —
 * so we scan rather than guessing the ref. Returns the matching key
 * (or null) so the diagnostic can include it in technical details when
 * useful.
 */
function findPkceVerifierKey(): string | null {
  if (typeof window === 'undefined') return null
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('sb-') && k.endsWith('-auth-token-code-verifier')) {
        const value = localStorage.getItem(k)
        if (value && value.length > 0) return k
      }
    }
  } catch {
    // Private mode / storage disabled — treat as missing.
  }
  return null
}

function formatExchangeError(err: { name: string; message: string; status?: number }): string {
  const lines = [`name: ${err.name}`, `message: ${err.message}`]
  if (err.status !== undefined) lines.push(`status: ${err.status}`)
  return lines.join('\n')
}

function truncate(s: string | null, n: number): string {
  if (!s) return ''
  return s.length <= n ? s : s.slice(0, n) + '…'
}
